import React, { useState, useEffect, useMemo, useContext, useRef } from "react";
import { ProductContext } from "./context/ProductContext";
import { useCategories } from "./context/CategoriesContext";
import { useCart } from "./context/CartContext";
import { useNavigate } from 'react-router-dom';
import { GripVertical, ChevronRight, ChevronLeft, ShoppingCart } from 'lucide-react';
import toast from 'react-hot-toast';

const MostLovedProducts = () => {
  const navigate = useNavigate();
  const categoriesScrollRef = useRef(null);

  const [activeCategory, setActiveCategory] = useState("all");
  const [currentSlide, setCurrentSlide] = useState(0);
  const [productsPerView, setProductsPerView] = useState(4);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);
  const [hoveredProduct, setHoveredProduct] = useState(null);
  const [showLeftFade, setShowLeftFade] = useState(false);
  const [showRightFade, setShowRightFade] = useState(false);
  const [canScroll, setCanScroll] = useState(false);

  const { products, productsLoading } = useContext(ProductContext);
  const { categories, loading: categoriesLoading } = useCategories();
  const { addToCart } = useCart();

  const categoryMap = useMemo(() => {
    const map = {};
    categories.forEach((c) => {
      map[String(c.id)] = c.title;
    });
    return map;
  }, [categories]);

  const categoryTabs = useMemo(() => [
    { id: "all", label: "All Products" },
    ...categories.map((c) => ({
      id: String(c.id),
      label: c.title,
    })),
  ], [categories]);

  const filteredProducts = useMemo(() => {
    if (activeCategory === "all") return products || [];
    return products.filter(
      (p) => String(p.categoryId) === String(activeCategory)
    );
  }, [products, activeCategory]);

  // Check scroll state and fade indicators
  const checkScrollState = () => {
    if (!categoriesScrollRef.current) return;

    const { scrollLeft, scrollWidth, clientWidth } = categoriesScrollRef.current;
    const canScrollHorizontally = scrollWidth > clientWidth;

    setCanScroll(canScrollHorizontally);
    setShowLeftFade(scrollLeft > 0);
    setShowRightFade(scrollLeft < scrollWidth - clientWidth - 1);
  };

  // Drag scroll handlers
  const handleMouseDown = (e) => {
    if (!categoriesScrollRef.current) return;

    setIsDragging(true);
    setStartX(e.pageX - categoriesScrollRef.current.offsetLeft);
    setScrollLeft(categoriesScrollRef.current.scrollLeft);

    // Prevent text selection during drag
    e.preventDefault();
  };

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isDragging || !categoriesScrollRef.current) return;

      e.preventDefault();
      const x = e.pageX - categoriesScrollRef.current.offsetLeft;
      const walk = (x - startX) * 2; // Scroll speed multiplier
      categoriesScrollRef.current.scrollLeft = scrollLeft - walk;
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, startX, scrollLeft]);

  // Check scroll state on mount and scroll
  useEffect(() => {
    checkScrollState();

    const scrollContainer = categoriesScrollRef.current;
    if (scrollContainer) {
      scrollContainer.addEventListener('scroll', checkScrollState);
      return () => scrollContainer.removeEventListener('scroll', checkScrollState);
    }
  }, [categoryTabs]);

  // Recheck scroll state on window resize
  useEffect(() => {
    const handleResize = () => {
      setTimeout(checkScrollState, 100);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (categories.length > 0 && activeCategory !== "all") {
      setActiveCategory("all");
    }
  }, [categories]);

  // Update products per view based on screen size
  useEffect(() => {
    const updateProductsPerView = () => {
      if (window.innerWidth < 768) {
        setProductsPerView(1); // Mobile: 1 product
      } else if (window.innerWidth < 1024) {
        setProductsPerView(2); // Tablet: 2 products
      } else if (window.innerWidth < 1280) {
        setProductsPerView(3); // Small desktop: 3 products
      } else {
        setProductsPerView(4); // Large desktop: 4 products
      }
    };

    updateProductsPerView();
    window.addEventListener('resize', updateProductsPerView);
    return () => window.removeEventListener('resize', updateProductsPerView);
  }, []);

  const maxSlides = Math.max(0, filteredProducts.length - productsPerView);
  const needsNavigation = filteredProducts.length > productsPerView;
  const hasProducts = filteredProducts.length > 0;

  const nextSlide = () => {
    setCurrentSlide((prev) => Math.min(prev + 1, maxSlides));
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => Math.max(prev - 1, 0));
  };

  const visibleProducts = filteredProducts.slice(currentSlide, currentSlide + productsPerView);

  // Reset slide when category changes
  const handleCategoryChange = (categoryName) => {
    if (!isDragging) { // Only change category if not dragging
      setActiveCategory(categoryName);
      setCurrentSlide(0);
    }
  };

  // Get responsive card width class based on products per view
  const getCardWidthClass = () => {
    switch (productsPerView) {
      case 1:
        return 'w-full max-w-sm mx-auto'; // Mobile: full width with max constraint
      case 2:
        return 'w-full max-w-sm'; // Tablet: half width with max constraint
      case 3:
        return 'w-full max-w-sm'; // Small desktop: third width with max constraint
      case 4:
      default:
        return 'w-full max-w-sm'; // Large desktop: quarter width with max constraint
    }
  };

  // Calculate discount percentage
  const calculateDiscount = (originalPrice, salePrice) => {
    if (!originalPrice || !salePrice) return null;
    const original = parseFloat(originalPrice);
    const sale = parseFloat(salePrice);
    if (original > sale) {
      return Math.round(((original - sale) / original) * 100);
    }
    return null;
  };

  // Enhanced Product Card Component
  const EnhancedProductCard = ({ product, index }) => {
    const [isHovered, setIsHovered] = useState(false);
    const isOnSale = product.offerPrice > 0 && product.offerPrice < product.price;
    const discountPercentage = calculateDiscount(product.price, product.offerPrice);

    // Alternative images for hover effect
    const hoverImages = {

    };

    const currentImage = isHovered && hoverImages[product.image] ? hoverImages[product.image] : product.image;

    const handleCardClick = (e) => {
      // Don't navigate if clicking on the cart button
      if (e.target.closest('button[aria-label*="cart"]')) {
        return;
      }
      navigate(`/product/${product.id}`);
    };

    const handleCartClick = (e) => {
      e.stopPropagation();

      const user = JSON.parse(localStorage.getItem("user"));

      if (!user) {
        navigate("/signin");
        return;
      }

      try {
        // Get minimum order quantity
        const minimumOrderQty = product?.minimumOrderQty || product?.min_order_qty || 1;

        // Determine the display price (use offer price if available, otherwise original price)
        const displayPrice = isOnSale ? product.offerPrice : product.price;

        // Get category name from categoryMap, fallback to categoryId if not found
        const categoryName = categoryMap[String(product.categoryId)] || product.categoryId || 'Uncategorized';

        // Add product to cart with category name as categoryId
        addToCart({
          id: product.id,
          name: product.name,
          price: displayPrice,
          image: product.image,
          categoryId: categoryName, // Send category name instead of ID
          min_order_qty: minimumOrderQty,
          minimumOrderQty: minimumOrderQty
        }, minimumOrderQty); // Use minimum order quantity instead of 1

        // Show success toast with quantity information
        toast.success(`${minimumOrderQty > 1 ? `${minimumOrderQty} × ` : ''}${product.name} added to cart!`, {
          icon: <ShoppingCart size={16} className="text-green-600" />,
          duration: 2000,
        });
      } catch (error) {
        console.error('Error adding to cart:', error);
        toast.error('Failed to add item to cart');
      }
    };

    return (
      <article
        className="flex flex-col h-full p-4 mx-auto w-full bg-white rounded-xl shadow-md hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1 cursor-pointer group"
        onClick={handleCardClick}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        role="article"
        aria-labelledby={`product-${product.id}-title`}
      >
        {/* Discount Ribbon */}
        {discountPercentage && (
          <div className="absolute top-0 left-4 z-20 transform translate-y-0">
            <div className="relative">
              {/* Main ribbon body */}
              <div className="relative w-12 bg-gradient-to-b from-red-500 via-red-600 to-red-700 shadow-2xl">
                {/* Top rounded cap */}
                <div className="w-full h-0 bg-gradient-to-b from-red-400 to-red-500 rounded-t-lg"></div>

                {/* Content area */}
                <div className="px-1 py-3 text-center relative overflow-hidden">
                  {/* Background pattern */}
                  <div className="absolute inset-0 opacity-10">
                    <div className="w-full h-full bg-gradient-to-br from-white via-transparent to-transparent"></div>
                  </div>

                  {/* Percentage number */}
                  <div className="relative z-10 text-white font-black text-xl leading-none mb-1 drop-shadow-lg">
                    {discountPercentage}%
                  </div>

                  {/* OFF text */}
                  <div className="relative z-10 text-white font-bold text-xs tracking-wider drop-shadow-md">
                    OFF
                  </div>

                  {/* Shine effect */}
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-20 transform -skew-y-12 animate-shine-vertical"></div>
                </div>

                {/* V-shaped bottom */}
                <div className="relative">
                  <div className="w-0 h-0 mx-auto border-l-8 border-r-8 border-t-6 border-l-red-700 border-r-red-700 border-t-red-700"></div>
                  <div className="absolute top-0 left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-6 border-r-6 border-t-4 border-l-red-800 border-r-red-800 border-t-red-800"></div>
                </div>

                {/* Side shadows for 3D effect */}
                <div className="absolute inset-y-0 -left-1 w-1 bg-gradient-to-b from-red-800 to-red-900 opacity-60 rounded-l-sm"></div>
                <div className="absolute inset-y-0 -right-1 w-1 bg-gradient-to-b from-red-300 to-red-400 opacity-40 rounded-r-sm"></div>
              </div>

              {/* Outer glow */}
              <div className="absolute inset-0 bg-red-500 opacity-30 blur-lg scale-100 rounded-lg"></div>

              {/* Floating sparkles */}
              <div className="absolute -top-1 -right-1 w-2 h-2 bg-yellow-300 rounded-full animate-ping opacity-75"></div>
              <div className="absolute top-6 -left-2 w-1.5 h-1.5 bg-yellow-200 rounded-full animate-pulse opacity-60"></div>
            </div>
          </div>
        )}

        {/* Image Container */}
        <div className="relative overflow-hidden mb-4 rounded-lg">
          <img
            src={currentImage}
            alt={product.name}
            className="object-contain w-full aspect-[0.85] transition-all duration-500 group-hover:scale-105"
            loading="lazy"
          />

          {/* Cart Icon - Top Right */}
          <button
            onClick={handleCartClick}
            className="absolute top-1 right-1 z-10 w-10 h-10 bg-black hover:bg-gray-800 flex items-center justify-center shadow-md hover:shadow-lg transition-all duration-200 transform hover:scale-110 group/cart"
            aria-label={`Add ${product?.minimumOrderQty || product?.min_order_qty || 1} × ${product.name} to cart`}
            title={`Add ${product?.minimumOrderQty || product?.min_order_qty || 1} units to cart${(product?.minimumOrderQty || product?.min_order_qty || 1) > 1 ? ' (minimum order quantity)' : ''}`}
          >
            <ShoppingCart
              size={22}
              className="text-white group-hover/cart:text-gray-200 transition-colors duration-200"
            />
            {/* Show minimum quantity badge if greater than 1 */}
            {/* {(product?.minimumOrderQty || product?.min_order_qty || 1) > 1 && (
              <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-bold">
                {product?.minimumOrderQty || product?.min_order_qty || 1}
              </span>
            )} */}
          </button>

          {/* Overlay effect on hover */}
          <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-5 transition-all duration-300 rounded-lg"></div>
        </div>

        {/* Content Container */}
        <div className="flex flex-col flex-1 justify-between">
          {/* Title and Price Section */}
          <div className="flex items-start justify-between gap-2 mb-3">
            <h3
              id={`product-${product.id}-title`}
              className="flex-1 text-base font-semibold text-black capitalize group-hover:text-zinc-700 transition-colors duration-200 truncate leading-tight"
            >
              {product.name}
            </h3>
            <div className="flex items-end gap-2 flex-shrink-0">
              <span
                className={`text-lg font-bold transition-colors duration-200 ${
                  isOnSale
                    ? 'text-red-500 group-hover:text-red-600'
                    : 'text-black group-hover:text-zinc-700'
                }`}
                aria-label={`${isOnSale ? 'Sale' : 'Current'} price ₹${isOnSale ? product.offerPrice : product.price}`}
              >
                ₹{isOnSale ? product.offerPrice : product.price}
              </span>
              {isOnSale && (
                <span
                  className="text-md text-gray-500 line-through"
                  aria-label={`Original price ₹${product.price}`}
                >
                  ₹{product.price}
                </span>
              )}
            </div>
          </div>

          {/* Category Section */}
          <div className="flex flex-col gap-1">
            <p className="text-xs text-gray-500 truncate">
              {categoryMap[String(product.categoryId)] || "Uncategorized"}
            </p>
            {/* Show minimum order quantity if greater than 1 */}
            {(product?.minimumOrderQty || product?.min_order_qty || 1) > 1 && (
              <p className="text-xs text-blue-600 font-medium">
                Min. order: {product?.minimumOrderQty || product?.min_order_qty || 1} units
              </p>
            )}
          </div>
        </div>

        {/* Custom CSS for animations */}
        <style jsx>{`
          @keyframes shine-vertical {
            0% { transform: translateY(-100%) skewY(-12deg); }
            100% { transform: translateY(200%) skewY(-12deg); }
          }

          .animate-shine-vertical {
            animation: shine-vertical 3s infinite;
          }

          @keyframes float {
            0%, 100% { transform: translateY(0px); }
            50% { transform: translateY(-4px); }
          }

          .animate-float {
            animation: float 2s ease-in-out infinite;
          }
        `}</style>
      </article>
    );
  };

  return (
    <section className="mt-10 mb-6 max-md:mt-10 px-4 max-w-7xl mx-auto" role="region" aria-labelledby="most-loved-heading">
      <h2 id="most-loved-heading" className="text-3xl md:text-4xl lg:text-5xl tracking-wide leading-snug text-neutral-700 font-semibold text-center max-md:max-w-full" style={{ fontFamily: 'Gloock, serif' }}>
        Most Loved by Our Customers
      </h2>
      <p className="mt-2 text-sm md:text-base font-light leading-6 md:leading-9 text-center text-gray-500 uppercase max-md:max-w-full">
        Your go-to picks for gifts, dEcor, apparel, and everyday personalization.
      </p>

      <div className="flex flex-col mt-6 md:mt-6 w-full">
        {/* Navigation Container */}
        <div className="flex flex-col lg:flex-row gap-4 lg:gap-6 justify-between items-start lg:items-center w-full text-sm md:text-base font-semibold">

          {/* Desktop Tabs with Drag Scroll - Hidden on mobile/tablet */}
          <div className="hidden lg:flex flex-1 overflow-hidden">
            <div className="w-full relative">
              {/* Scroll Indicator - Only show when scrollable */}
              {canScroll && (
                <div className="flex items-center justify-center mb-2">
                  <div className="flex items-center gap-2 px-3 py-1 bg-gray-100 rounded-full text-xs text-gray-600">
                    <GripVertical size={14} className="text-gray-400" />
                    <span>Drag to scroll</span>
                    <div className="flex gap-1">
                      <div className="w-1 h-1 bg-gray-400 rounded-full animate-pulse"></div>
                      <div className="w-1 h-1 bg-gray-400 rounded-full animate-pulse" style={{ animationDelay: '0.2s' }}></div>
                      <div className="w-1 h-1 bg-gray-400 rounded-full animate-pulse" style={{ animationDelay: '0.4s' }}></div>
                    </div>
                  </div>
                </div>
              )}

              {/* Categories Container with Fade Effects */}
              <div className="relative">
                {/* Left Fade Gradient */}
                {showLeftFade && (
                  <div className="absolute left-0 top-0 bottom-0 w-8 bg-gradient-to-r from-white via-white to-transparent z-10 pointer-events-none"></div>
                )}

                {/* Right Fade Gradient */}
                {showRightFade && (
                  <div className="absolute right-0 top-0 bottom-0 w-8 bg-gradient-to-l from-white via-white to-transparent z-10 pointer-events-none"></div>
                )}

                {/* Scrollable Categories */}
                <nav
                  ref={categoriesScrollRef}
                  className={`flex gap-2 md:gap-4 lg:gap-6 items-center overflow-x-auto scrollbar-hide pb-2 lg:pb-0 w-full select-none transition-all duration-200 ${
                    isDragging ? 'cursor-grabbing' : canScroll ? 'cursor-grab' : 'cursor-default'
                  } ${canScroll ? 'border border-gray-200 rounded-lg p-2 bg-gray-50/50' : ''}`}
                  style={{
                    scrollbarWidth: 'none',
                    msOverflowStyle: 'none',
                    WebkitOverflowScrolling: 'touch',
                    userSelect: 'none',
                    WebkitUserSelect: 'none',
                    MozUserSelect: 'none'
                  }}
                  aria-label="Product categories"
                  onMouseDown={handleMouseDown}
                  onSelectStart={(e) => e.preventDefault()}
                >
                  <style jsx>{`
                    nav::-webkit-scrollbar {
                      display: none;
                    }
                  `}</style>
                  {categoryTabs.map((category, index) => (
                    <button
                      key={category.id}
                      onClick={() => handleCategoryChange(category.id)}
                      onMouseDown={(e) => {
                        // Prevent drag when clicking on button
                        e.stopPropagation();
                      }}
                      className={`px-4 py-2 rounded-lg transition-all duration-200 hover:bg-gray-100 flex-shrink-0 whitespace-nowrap text-center select-none relative ${
                        activeCategory === category.id
                          ? "text-teal-800 bg-teal-50 border-b-2 border-teal-800 font-bold shadow-sm"
                          : "text-gray-600 hover:text-teal-600 hover:shadow-sm"
                      }`}
                      aria-pressed={activeCategory === category.id}
                      style={{
                        userSelect: 'none',
                        WebkitUserSelect: 'none',
                        MozUserSelect: 'none'
                      }}
                    >
                      {category.label}

                      {/* Active indicator dot */}
                      {activeCategory === category.id && (
                        <div className="absolute -top-1 -right-1 w-2 h-2 bg-teal-600 rounded-full animate-pulse"></div>
                      )}
                    </button>
                  ))}
                </nav>

                {/* Scroll Progress Indicator */}
                {canScroll && (
                  <div className="mt-2 w-full h-1 bg-gray-200 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-teal-600 rounded-full transition-all duration-300"
                      style={{
                        width: `${((categoriesScrollRef.current?.scrollLeft || 0) /
                          Math.max(1, (categoriesScrollRef.current?.scrollWidth || 1) - (categoriesScrollRef.current?.clientWidth || 1))) * 100}%`
                      }}
                    ></div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Mobile/Tablet Dropdown - Hidden on desktop */}
          <div className="lg:hidden w-full">
            <div className="relative">
              <select
                value={activeCategory}
                onChange={(e) => handleCategoryChange(e.target.value)}
                className="w-full px-4 py-3 text-gray-600 bg-white border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 bg-teal-50 border border-b-2 border-teal-800 appearance-none cursor-pointer"
                aria-label="Select product category"
              >
                {categoryTabs.map((category) => (
                  <option key={category.id} value={category.id}>
                    {category.label}
                  </option>
                ))}
              </select>
              {/* Custom dropdown arrow */}
              <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none">
                <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>
            </div>
          </div>

          {/* View All Button */}
          <div className="flex justify-center lg:justify-end w-full lg:w-auto mt-1 lg:mt-0">
            <button
              onClick={() => navigate('/products')}
              className="flex items-center justify-center gap-2 py-2 px-4 md:px-6 text-sm md:text-base text-white text-center rounded shadow-lg bg-neutral-800 hover:bg-neutral-700 focus:outline-none focus:ring-2 focus:ring-neutral-500 transition-colors duration-200 whitespace-nowrap flex-shrink-0"
              aria-label="View all products"
            >
              <span>View All</span>
            </button>
          </div>
        </div>

        {/* Carousel or Empty State */}
        <div className="relative mt-6 w-full">
          {!hasProducts ? (
            // Empty State
            <div className="flex flex-col items-center justify-center py-16 px-4">
              <div className="text-center">
                <div className="w-24 h-24 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
                  <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M9 5l7 7-7 7" />
                  </svg>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">No Products Available</h3>
                <p className="text-gray-500 max-w-md">
                  {activeCategory === "all"
                    ? "We're currently updating our product catalog. Please check back soon!"
                    : `No products found in the "${categoryTabs.find(cat => cat.id === activeCategory)?.label}" category.`
                  }
                </p>
              </div>
            </div>
          ) : (
            // Products Carousel
            <div className="flex items-center gap-2 md:gap-4">
              {/* Navigation Arrow Left - Only show if navigation is needed */}
              {needsNavigation && (
                <button
                  onClick={prevSlide}
                  disabled={currentSlide === 0}
                  className="flex justify-center items-center text-2xl md:text-3xl font-black text-center bg-white bg-opacity-80 hover:bg-opacity-100 shadow  text-gray-800 min-h-8 md:min-h-10 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200 flex-shrink-0"
                  aria-label="Previous products"
                >
                  <ChevronLeft/>
                </button>
              )}

              {/* Products Container */}
              <div className="flex-1 overflow-hidden min-h-[400px]">
                <div className={`grid gap-3 pb-3 md:gap-4 lg:gap-6 justify-items-center ${
                  productsPerView === 1 ? 'grid-cols-1' :
                  productsPerView === 2 ? 'grid-cols-1 md:grid-cols-2' :
                  productsPerView === 3 ? 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3' :
                  'grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4'
                }`}>
                  {visibleProducts.map((product, index) => (
                    <div key={product.id || index} className={getCardWidthClass()}>
                      <EnhancedProductCard product={product} index={index} />
                    </div>
                  ))}

                  {/* Fill empty slots to maintain grid structure */}
                  {visibleProducts.length < productsPerView && (
                    Array.from({ length: productsPerView - visibleProducts.length }).map((_, index) => (
                      <div key={`empty-${index}`} className={`${getCardWidthClass()} invisible`}>
                        <div className="w-full aspect-[0.78]"></div>
                      </div>
                    ))
                  )}
                </div>
              </div>

              {/* Navigation Arrow Right - Only show if navigation is needed */}
              {needsNavigation && (
                <button
                  onClick={nextSlide}
                  disabled={currentSlide >= maxSlides}
                  className="flex justify-center items-center text-2xl md:text-3xl font-black text-center bg-white bg-opacity-80 hover:bg-opacity-100 shadow text-gray-800 min-h-8 md:min-h-10 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200 flex-shrink-0"
                  aria-label="Next products"
                >
                 <ChevronRight/>
                </button>
              )}
            </div>
          )}

          {/* Carousel Indicators - Only show if navigation is needed */}
          {hasProducts && needsNavigation && maxSlides > 0 && (
            <div className="flex justify-center mt-4 space-x-2" role="tablist" aria-label="Carousel navigation">
              {Array.from({ length: maxSlides + 1 }).map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentSlide(index)}
                  className={`w-2 h-2 rounded-full transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-teal-500 ${
                    index === currentSlide ? 'bg-teal-600' : 'bg-gray-300 hover:bg-gray-400'
                  }`}
                  aria-label={`Go to slide ${index + 1}`}
                  role="tab"
                  aria-selected={index === currentSlide}
                />
              ))}
            </div>
          )}
        </div>

        {/* Mobile-specific: Show total count - Only if there are products */}
        {hasProducts && (
          <div className="flex justify-center mt-4 md:hidden">
            <span className="text-sm text-gray-500">
              {needsNavigation ? (
                `${currentSlide + 1} - ${Math.min(currentSlide + productsPerView, filteredProducts.length)} of ${filteredProducts.length} products`
              ) : (
                `${filteredProducts.length} product${filteredProducts.length !== 1 ? 's' : ''}`
              )}
            </span>
          </div>
        )}
      </div>
    </section>
  );
};

export default MostLovedProducts;
